import { makeAutoObservable } from "mobx";
import { delay } from "src/common/delay";
import { cvtError, onError } from "src/common/onError";
import { Cell2, isEqualCellValues } from "./Cell2";
import { isSymbolKey, KeyDef } from "./isSymbolKey";

type Direction = -1 | 0 | 1;

export class Sheet2Store {
  constructor() {
    makeAutoObservable(this);
  }

  init() {
    setTimeout(() => this.activateFirst(), 10);
  }

  cells: Record<string, Cell2> = {};

  protected setCell(cell: Cell2) {
    this.cells[cell.cellKey] = cell;
  }

  updatedKeys = new Set<string>();

  updateCell(cell: Cell2) {
    const { cellKey } = cell;
    const { updatedKeys } = this;
    // для того чтобы не вызывать постоянный rerender, стоит сначала проверить наличие ключа в списке обновляемых в данный момент ячеек
    if (updatedKeys.has(cellKey)) return;
    this.setCell(cell);
    updatedKeys.add(cellKey);
  }

  postUpdate() {
    const { updatedKeys } = this;
    const deadKeys = Object.keys(this.cells).filter(
      (key) => !updatedKeys.has(key),
    );
    deadKeys.forEach((key) => {
      delete this.cells[key];
    });
    this.updatedKeys.clear();
  }

  cellByKey(cellKey: string | null): Cell2 | null {
    return cellKey ? (this.cells[cellKey] ?? null) : null;
  }

  editing = false;

  activeKey: string | null = null;

  get activeCell(): Cell2 | null {
    return this.cellByKey(this.activeKey);
  }

  isViewMode(cellKey: string): boolean {
    return (
      !this.editing ||
      cellKey !== this.activeKey ||
      this.saving.has(cellKey) ||
      !!this.cellByKey(cellKey)?.viewOnly
    );
  }

  activateFirst() {
    const firstCell = Object.values(this.cells).find((cell) => !cell.disabled);
    this.activate(firstCell?.cellKey ?? null, false);
  }

  activate(cellKey: string | null, isEdit: boolean) {
    const prevCell = this.cellByKey(this.activeKey);
    if (prevCell?.cellKey === cellKey) {
      if (isEdit && !this.editing) {
        this.editing = !prevCell.viewOnly;
        this.activeCell?.focus(true);
      }
      return;
    }
    this.activeKey = cellKey;
    this.editing = false;
    const cell = this.cellByKey(cellKey);
    if (cell) {
      if (!cell.viewOnly) this.editing = isEdit;
      cell.focus(isEdit);
    }
  }

  stepTo(x: number, y: number): boolean {
    const newCell = this.findByPosition(x, y);
    if (newCell && !newCell.disabled) {
      this.activate(newCell.cellKey, false);
      return true;
    }
    return false;
  }

  stepToAvailable(
    startX: number,
    startY: number,
    dx: Direction = 0,
    dy: Direction = 0,
  ) {
    if (dx === 0 && dy === 0) return false;
    let x = startX + dx;
    let y = startY + dy;
    let cell = this.findByPosition(x, y);
    while (cell) {
      if (!cell.disabled) {
        this.activate(cell.cellKey, false);
        return true;
      }
      x += dx;
      y += dy;
      cell = this.findByPosition(x, y);
    }
    return false;
  }

  findRelative(
    x: number,
    y: number,
    to: "mostLeft" | "mostRight",
  ): Cell2 | null {
    const cells = Object.values(this.cells);
    let dstCell: Cell2 | null = null;
    if (to === "mostLeft") {
      cells.forEach((cell) => {
        const { pos, disabled } = cell;
        if (pos.y === y && (!dstCell || pos.x < dstCell.pos.x) && !disabled) {
          dstCell = cell;
        }
      });
    } else if (to === "mostRight") {
      cells.forEach((cell) => {
        const { pos, disabled } = cell;
        if (pos.y === y && (!dstCell || pos.x > dstCell.pos.x) && !disabled) {
          dstCell = cell;
        }
      });
    }
    return dstCell;
  }

  findByPosition(x: number, y: number) {
    return Object.values(this.cells).find(
      ({ pos }) => pos.x === x && pos.y === y,
    );
  }

  // -- keys
  onArrow(dx: Direction, dy: Direction) {
    if (this.editing) return false;
    const { activeCell } = this;
    if (activeCell) {
      return this.stepToAvailable(activeCell.pos.x, activeCell.pos.y, dx, dy);
    }
    return false;
  }

  onSkipToBound(mode: "mostLeft" | "mostRight") {
    if (this.editing) return;
    const { activeCell } = this;
    if (activeCell) {
      const dst = this.findRelative(activeCell.pos.x, activeCell.pos.y, mode);
      if (dst) {
        this.activate(dst.cellKey, false);
      }
    }
  }

  onEnter(dy: -1 | 1 = 1) {
    const { activeCell } = this;
    if (activeCell) {
      // Сейчас требования такие, что при нажатии на Enter всегда переход на ячейку ниже
      if (!this.stepToAvailable(activeCell.pos.x, activeCell.pos.y, 0, dy))
        this.blur(activeCell);
    }
  }

  onDelete() {
    const { activeCell, editing } = this;
    if (!editing && activeCell) {
      this.setCurCellValue(activeCell.cellKey, activeCell.emptyValue);
      this.blur(activeCell);
    }
  }

  onBackspace() {
    this.onDelete();
  }

  onKeyDown(cellKey: string, e: KeyDef) {
    // Чтобы не обрабатывать какие-то случаи (н.р. Enter в TextArea), надо их анализировать снаружи и не вызывать функцию
    const inView = this.isViewMode(cellKey);
    if (inView && isSymbolKey(e)) {
      this.activate(cellKey, true);
      // после активации ячейки нажатый символ передаётся в компонент.
      // Input после фокуса выделяет всё своё содержимое, поэтому новый символ замещает содержимое.
    } else if (e.key === "ArrowDown") {
      this.onArrow(0, 1);
    } else if (e.key === "ArrowUp") {
      this.onArrow(0, -1);
    } else if (e.key === "ArrowLeft") {
      if (this.onArrow(-1, 0)) e.preventDefault(); // это чтобы при перемещении фокуса дополнительно не смещался скролл от дефолтной реакции на стрелки
    } else if (e.key === "ArrowRight") {
      if (this.onArrow(1, 0)) e.preventDefault();
    } else if (e.key === "Enter") {
      this.onEnter(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      this.resetCurValue(cellKey, true);
    } else if (e.key === "Delete") {
      this.onDelete();
    } else if (e.key === "Backspace") {
      this.onBackspace();
    } else if (e.key === "F2") {
      this.activate(this.activeKey, true);
    } else if (e.key === "Home") {
      this.onSkipToBound("mostLeft");
    } else if (e.key === "End") {
      this.onSkipToBound("mostRight");
    }
  }

  cellSrcValues: Record<string, unknown> = {};

  cellCurValues: Record<string, unknown> = {};

  setSrcCellValue(cellKey: string, value: unknown) {
    this.cellSrcValues[cellKey] = value;
    this.setCurCellValue(cellKey, value);
  }

  setCurCellValue(cellKey: string, value: unknown) {
    this.cellCurValues[cellKey] = value;
  }

  resetCurValue(cellKey: string, stopEdit: boolean) {
    this.setCurCellValue(cellKey, this.cellSrcValues[cellKey]);
    if (stopEdit) {
      this.editing = false;
      this.clearError(cellKey);
    }
  }

  saving = new Set<string>();

  startSaving(cellKey: string) {
    this.saving.add(cellKey);
  }

  stopSaving(cellKey: string) {
    this.saving.delete(cellKey);
  }

  errors: Record<string, string> = {};

  setError(cellKey: string, errMsg: string) {
    this.errors[cellKey] = errMsg;
  }

  clearError(cellKey: string) {
    delete this.errors[cellKey];
  }

  /**
   * Для прямого изменения значений из бизнес-логики
   * @param cellKey
   * @param newValue
   */
  setValueByKey(cellKey: string, newValue: unknown) {
    this.setCurCellValue(cellKey, newValue);
    this.saveCell(this.cellByKey(cellKey)).catch(onError);
  }

  /**
   * Если нужно проделать какую-то операцию с ячейкой
   */
  async cellTask(cellKey: string, task: () => Promise<void>) {
    try {
      this.clearError(cellKey);
      this.startSaving(cellKey);
      await delay(1); // Иногда компонент что-то меняет после потери фокуса. н.р InputNumber контролирует правильность
      await task();
    } catch (e) {
      this.setError(cellKey, cvtError(e).message);
    } finally {
      this.stopSaving(cellKey);
    }
  }

  async saveCell(cell: Cell2 | null) {
    if (!cell) return;
    const { cellKey: key, validate, save } = cell;
    this.cellTask(key, async () => {
      const srcValue = this.cellSrcValues[key];
      const curValue = this.cellCurValues[key];
      if (isEqualCellValues(curValue, srcValue, cell)) return;
      if (validate) {
        await validate(curValue);
      }
      const guardValue = (this.guard[key] ?? 0) + 1;
      this.guard[key] = guardValue;
      const callback = await save(curValue);
      if (this.guard[key] === guardValue) {
        callback();
      }
    });
  }

  blur(cell: Cell2 | null) {
    this.editing = false;
    this.saveCell(cell).catch(onError);
  }

  guard: Record<string, number> = {};
}
